import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { Alert, Button, Card, Space, Typography } from "antd";
import { CSSProperties, createContext, ReactNode, useContext } from "react";
import styled from "styled-components";
import { EtapaCadastroCota, FluxoResposta } from "./cadastroCotaUsuario.types";

const { Paragraph, Text } = Typography;

export const OnboardingNavigationContext = createContext<{
  etapaExibida?: EtapaCadastroCota;
  onVoltar?: () => void;
  etapaAnteriorNome?: string;
}>({});

export const STAGE_ITEMS = [
  { title: "Propriedade" },
  { title: "Titularidade" },
  { title: "Disponibilidade" },
  { title: "Períodos" },
  { title: "Venda" },
];

export const DESTINACAO_OPTIONS = [
  { label: "Uso próprio", value: "USO_PROPRIO" },
  { label: "Emprestar", value: "CEDIDO_EMPRESTADO" },
  { label: "Pool de locação", value: "POOL_LOCACAO" },
  { label: "Negociar por conta própria", value: "NEGOCIACAO_PROPRIA" },
];

export const TIPO_VINCULO_OPTIONS = [
  {
    titulo: "Sou o titular",
    descricao: "A propriedade está em meu nome.",
    value: "TITULAR",
  },
  {
    titulo: "Sou cônjuge do titular",
    descricao: "A propriedade está em nome do meu marido ou esposa.",
    value: "CONJUGE",
  },
  {
    titulo: "Sou familiar do titular",
    descricao: "Estou cadastrando a propriedade para um familiar.",
    value: "FAMILIAR",
  },
  {
    titulo: "Sou revendedor",
    descricao: "Administro ou comercializo a propriedade para o titular.",
    value: "REVENDEDOR",
  },
];

export const DEFINICAO_PERIODOS_OPTIONS = [
  { label: "Cronograma fixo", value: "CRONOGRAMA_FIXO" },
  { label: "Escolha periódica", value: "ESCOLHA_PERIODICA" },
  { label: "Outro", value: "OUTRO" },
];

export const MOTIVO_OPTIONS = [
  { label: "Pool de locação", value: "POOL_LOCACAO" },
  {
    label: "Empreendimento não operacional",
    value: "EMPREENDIMENTO_NAO_OPERACIONAL",
  },
  { label: "Outro", value: "OUTRO" },
];

export const monthOptions = [
  { label: "Janeiro", value: 1 },
  { label: "Fevereiro", value: 2 },
  { label: "Março", value: 3 },
  { label: "Abril", value: 4 },
  { label: "Maio", value: 5 },
  { label: "Junho", value: 6 },
  { label: "Julho", value: 7 },
  { label: "Agosto", value: 8 },
  { label: "Setembro", value: 9 },
  { label: "Outubro", value: 10 },
  { label: "Novembro", value: 11 },
  { label: "Dezembro", value: 12 },
];

export const yearOptions = Array.from({ length: 10 }, (_, index) => {
  const year = dayjs().year() + index;
  return { label: String(year), value: year };
});

export function getApiErrorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as
      | { message?: unknown }
      | undefined;
    const message = responseData?.message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

export function isForbiddenError(error: unknown) {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

export function isUnauthorizedError(error: unknown) {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

export function isNotFoundError(error: unknown) {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

export function getEtapaAtualResumo(etapaAtual: EtapaCadastroCota) {
  switch (etapaAtual) {
    case "AGUARDANDO_EMPREENDIMENTO":
    case "AGUARDANDO_APARTAMENTO":
    case "AGUARDANDO_IDENTIFICACAO_COTA":
      return { nome: "Propriedade" };
    case "AGUARDANDO_TITULARIDADE":
      return { nome: "Titularidade" };
    case "AGUARDANDO_DISPONIBILIDADE":
      return { nome: "Disponibilidade" };
    case "AGUARDANDO_PERIODOS":
      return { nome: "Disponibilidade" };
    case "AGUARDANDO_VENDA":
      return { nome: "Venda" };
    case "AGUARDANDO_CONTRATACAO":
      return { nome: "Ativação" };
    case "PAGAMENTO_PENDENTE":
      return { nome: "Ativação" };
    case "PAGAMENTO_CONFIRMADO":
      return { nome: "Ativação" };
    case "UTILIZAR_VAGA_ASSINATURA":
      return { nome: "Ativação" };
    case "AGUARDANDO_OPORTUNIDADES_HOSPEDAGEM":
      return { nome: "Oportunidades" };
    case "CONCLUIDO":
      return { nome: "Concluído" };
    default:
      return { nome: "Cadastro" };
  }
}

export function formatPeriodoLabel(checkIn: string, checkOut: string) {
  const start = dayjs(checkIn);
  const end = dayjs(checkOut);

  if (start.isValid() && end.isValid()) {
    const nomeMes = (data: Dayjs) =>
      new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(
        new Date(data.year(), data.month(), 1)
      );

    if (start.year() === end.year() && start.month() === end.month()) {
      return `${start.date()} a ${end.date()} de ${nomeMes(start)} de ${start.year()}`;
    }

    if (start.year() === end.year()) {
      return `${start.date()} de ${nomeMes(start)} a ${end.date()} de ${nomeMes(end)} de ${start.year()}`;
    }

    return `${start.date()} de ${nomeMes(start)} de ${start.year()} a ${end.date()} de ${nomeMes(end)} de ${end.year()}`;
  }

  return `${checkIn} até ${checkOut}`;
}

export function toIsoDate(value?: Dayjs | null) {
  if (!value) {
    return undefined;
  }

  return value.format("YYYY-MM-DD");
}

export function filterSelectOption(input: string, option?: { label?: string }) {
  return String(option?.label ?? "")
    .toLowerCase()
    .includes(input.toLowerCase());
}

export const PageSection = styled.div`
  width: min(1040px, calc(100% - 32px));
  margin: 0 auto;
  padding: 24px 0 56px;

  @media (max-width: 640px) {
    width: calc(100% - 24px);
    padding-top: 16px;
  }
`;

export const StepCard = styled(Card)`
  border-radius: 24px;
  border: 1px solid #d9e4e8;
  box-shadow: 0 20px 40px rgba(0, 54, 68, 0.08);

  .ant-card-body {
    padding: 24px;
  }
`;

export const SecondaryCard = styled(Card)`
  border-radius: 20px;
  border: 1px solid #e8eff2;
  background: #f9fbfc;
`;

export const CardsGrid = styled.div`
  display: grid;
  gap: 16px;
`;

export const StepHeader = styled.div`
  margin-bottom: 24px;
`;

export const Hero = styled.div`
  background:
    radial-gradient(circle at top left, rgba(225, 177, 44, 0.18), transparent 35%),
    linear-gradient(135deg, #003644 0%, #0d4f61 100%);
  color: #fff;
  border-radius: 28px;
  padding: 28px;
  margin-bottom: 20px;
  box-shadow: 0 24px 48px rgba(0, 54, 68, 0.18);
`;

export const HeroTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

export const ActionsRow = styled(Space)`
  width: 100%;
  justify-content: flex-end;
`;

export function OnboardingFrame({
  fluxo,
  title,
  description,
  children,
  notice,
  compactHero = false,
}: {
  fluxo: FluxoResposta;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  notice?: string;
  compactHero?: boolean;
}) {
  const { etapaExibida } = useContext(OnboardingNavigationContext);
  const etapaResumo =
    fluxo.noAtual === "EXISTENCIA_PERIODOS" ||
    fluxo.noAtual === "INFORMAR_PERIODOS" ||
    fluxo.noAtual === "LEMBRETE_PERIODOS"
      ? { nome: "Períodos" }
      : getEtapaAtualResumo(etapaExibida ?? fluxo.etapaAtual);
  return (
    <PageSection>
      <Hero style={compactHero ? { padding: 22, marginBottom: 16 } : undefined}>
        <HeroTop>
          <div>
            <Typography.Title
              level={2}
              style={{ color: "#fff", margin: "0 0 8px" }}
            >
              {title}
            </Typography.Title>
            {description ? (
              <Paragraph
                style={{
                  color: "rgba(255,255,255,0.9)",
                  margin: 0,
                  fontSize: 16,
                  lineHeight: 1.65,
                }}
              >
                {description}
              </Paragraph>
            ) : null}
          </div>
          <Card
            bordered={false}
            style={{
              minWidth: compactHero ? 206 : 220,
              borderRadius: 20,
              background: "rgba(255,255,255,0.12)",
            }}
          >
            <Space direction="vertical" size={4}>
              <Text style={{ color: "#fff" }}>Etapa atual</Text>
              <Text strong style={{ color: "#fff" }}>
                {etapaResumo.nome}
              </Text>
            </Space>
          </Card>
        </HeroTop>
      </Hero>

      {notice ? (
        <Alert
          type="info"
          showIcon
          message={notice}
          style={{ marginBottom: 20, borderRadius: 16 }}
        />
      ) : null}

      <StepCard>{children}</StepCard>
    </PageSection>
  );
}

export function SubmitButton({
  label,
  loading,
  htmlType = "submit",
  disabled,
  disabledStyle,
}: {
  label: string;
  loading?: boolean;
  htmlType?: "button" | "submit";
  disabled?: boolean;
  disabledStyle?: CSSProperties;
}) {
  const { onVoltar } = useContext(OnboardingNavigationContext);

  return (
    <Space>
      {onVoltar ? (
        <Button htmlType="button" onClick={onVoltar} size="large" disabled={loading}>
          Voltar
        </Button>
      ) : null}
      <Button
      type="primary"
      size="large"
      htmlType={htmlType}
      loading={loading}
      disabled={disabled}
      style={{
        minWidth: 180,
        borderRadius: 999,
        background: "#003644",
        ...(disabled ? disabledStyle : {}),
      }}
    >
      {label}
    </Button>
    </Space>
  );
}
